/* Маршруты: вход, CRM (по ролям), публичные страницы клиента (предложение, тестовая оплата). */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { Me } from "@okno/shared";
import { api, setUnauthorizedHandler } from "./lib/api";
import { FullScreenMessage, SessionProvider, useSession } from "./lib/session";
import { LoginScreen } from "./screens/LoginScreen";
import { allowedNav, Shell } from "./Shell";
import { LayersProvider } from "./ui/layers";
import { Spinner } from "./ui/kit";
import { ToastProvider } from "./ui/toast";

const ShiftScreen = lazy(() => import("./screens/shift/ShiftScreen").then((m) => ({ default: m.ShiftScreen })));
const TasksScreen = lazy(() => import("./screens/tasks/TasksScreen").then((m) => ({ default: m.TasksScreen })));
const CalendarScreen = lazy(() => import("./screens/calendar/CalendarScreen").then((m) => ({ default: m.CalendarScreen })));
const OrdersScreen = lazy(() => import("./screens/orders/OrdersScreen").then((m) => ({ default: m.OrdersScreen })));
const ClientsScreen = lazy(() => import("./screens/clients/ClientsScreen").then((m) => ({ default: m.ClientsScreen })));
const OffersScreen = lazy(() => import("./screens/offers/OffersScreen").then((m) => ({ default: m.OffersScreen })));
const AnalyticsScreen = lazy(() => import("./screens/analytics/AnalyticsScreen").then((m) => ({ default: m.AnalyticsScreen })));
const SettingsScreen = lazy(() => import("./screens/settings/SettingsScreen").then((m) => ({ default: m.SettingsScreen })));
const AuditScreen = lazy(() => import("./screens/audit/AuditScreen").then((m) => ({ default: m.AuditScreen })));
const OfferPage = lazy(() => import("./screens/public/OfferPage").then((m) => ({ default: m.OfferPage })));
const MockPayPage = lazy(() => import("./screens/public/MockPayPage").then((m) => ({ default: m.MockPayPage })));

const SCREENS: Record<string, React.ComponentType> = {
  shift: ShiftScreen,
  tasks: TasksScreen,
  calendar: CalendarScreen,
  orders: OrdersScreen,
  clients: ClientsScreen,
  offers: OffersScreen,
  analytics: AnalyticsScreen,
  settings: SettingsScreen,
  audit: AuditScreen,
};

/** Маршруты CRM, доступные роли; неизвестный путь ведёт на первый доступный экран. */
function CrmRoutes() {
  const { role } = useSession();
  const nav = allowedNav(role);
  return (
    <Routes>
      <Route element={<Shell />}>
        {nav.map((n) => {
          const C = SCREENS[n.id];
          return (
            <Route
              key={n.id}
              path={"/" + n.id}
              element={
                <Suspense fallback={<Spinner />}>
                  <C />
                </Suspense>
              }
            />
          );
        })}
        <Route path="*" element={<Navigate to={"/" + nav[0].id} replace />} />
      </Route>
    </Routes>
  );
}

function Crm() {
  const qc = useQueryClient();
  const loc = useLocation();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<Me>("/auth/me"),
    retry: false,
    staleTime: 5 * 60_000,
  });
  // Не qc.clear(): он удалил бы и запрос ["me"], на который подписан этот экран, — выход не отрисовался бы.
  const onLogout = useCallback(() => {
    qc.setQueryData(["me"], null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
  }, [qc]);

  useEffect(() => {
    setUnauthorizedHandler(onLogout);
  }, [onLogout]);

  if (me.isLoading) return <FullScreenMessage title="Загрузка…" text="" />;
  if (!me.data) return <LoginScreen onLogin={(m) => qc.setQueryData(["me"], m)} from={loc.pathname} />;
  return (
    <SessionProvider key={me.data.id} me={me.data} onLogout={onLogout}>
      {/* слои CRM живут внутри сессии: карточкам нужен useSession() */}
      <LayersProvider>
        <CrmRoutes />
      </LayersProvider>
    </SessionProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <LayersProvider>
          <Routes>
            <Route
              path="/o/:token"
              element={
                <Suspense fallback={<Spinner />}>
                  <OfferPage />
                </Suspense>
              }
            />
            <Route
              path="/pay/mock/:paymentId"
              element={
                <Suspense fallback={<Spinner />}>
                  <MockPayPage />
                </Suspense>
              }
            />
            <Route path="*" element={<Crm />} />
          </Routes>
        </LayersProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
